"use client";

import {
  ChevronDown,
  ArrowRight,
  TrendingUp,
  Factory,
  Globe,
  Coins,
  Landmark,
  ClipboardList,
  BookOpen,
  Scale,
  ArrowLeftRight,
  CalendarDays,
  CalendarRange,
  CalendarClock,
  Award,
  Banknote,
  Package,
  Recycle,
  Users,
  UserRound,
  ShieldCheck,
  type LucideIcon,
} from "lucide-react";
import type { LinkTileGridComponent } from "../../types";
import { GuardedLink } from "../../runtime/DevPlaceholder";
import { RuntimeImage, useLocale } from "../../runtime/RendererRuntime";
import { resolveImageValue } from "../../utils/imageOptions";

const SOFT_COLS: Record<number, string> = {
  2: "lg:grid-cols-2",
  3: "lg:grid-cols-3",
  4: "lg:grid-cols-4",
};

/** Named lucide icons selectable per tile via `item.icon`. Kept small and
 *  domain-relevant (statistics sections + publication frequencies); unknown
 *  names fall back to the chevron so existing pages are unaffected. */
const TILE_ICONS: Record<string, LucideIcon> = {
  TrendingUp,
  Factory,
  Globe,
  Coins,
  Landmark,
  ClipboardList,
  BookOpen,
  Scale,
  ArrowLeftRight,
  CalendarDays,
  CalendarRange,
  CalendarClock,
  Award,
  Banknote,
  Package,
  Recycle,
  Users,
  UserRound,
  ShieldCheck,
};

function resolvePrimaryIcon(label: string, icon?: string): LucideIcon {
  if (icon && TILE_ICONS[icon]) return TILE_ICONS[icon];

  const normalized = label
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");

  if (normalized.includes("recyclage")) return Recycle;
  if (normalized.includes("realisation")) return Award;
  if (normalized.includes("production")) return Factory;
  if (normalized.includes("produit")) return Package;
  if (normalized.includes("fiduciaire") || normalized.includes("billet")) return Banknote;
  return ChevronDown;
}

/**
 * Two-variant link tile grid.
 *
 *  - `primary` (default) — the classic blue background hero tile with a
 *    centred label and chevron icon. Untouched: existing pages render the
 *    same as before.
 *  - `soft` — quieter editorial card with the title at the top, a per-card
 *    description below (which doubles as SEO copy), and a subtle slate
 *    border that thickens on hover. Switches to a 4-column layout on `lg+`
 *    so a 4-link "pages connexes" row sits on one line; falls back to
 *    2 columns on `sm` and 1 column on mobile.
 */
export function LinkTileGridRenderer({ component }: { component: LinkTileGridComponent }) {
  const english = useLocale().startsWith("en");
  const { items, variant = "primary", title, columns } = component.content;

  if (variant === "band") {
    // Bande de clôture pleine largeur : MÊME gabarit que « Autres secteurs
    // économiques » des pages secteur (/statistiques/{secteur}) — fond #F3F7FC
    // bord à bord (mx-[calc(50%-50vw)], comme SplitImageCallout), cartes
    // BLANCHES à liseré bleu, icône en filigrane, lien « Lire la suite → »
    // ambre. Le padding bas interne (pb-16) remplace celui de la page quand le
    // composant est dernier et que la page pose lastWidgetFlushBottom.
    const cols = SOFT_COLS[Number(columns)] ?? "lg:grid-cols-3";
    return (
      <section className="mx-[calc(50%-50vw)] bg-[#F3F7FC] pt-10 pb-16">
        <div className="mx-auto max-w-[75rem] px-4 md:px-8">
          {title ? (
            <h2 className="text-2xl font-bold text-[#005CA9]" style={{ fontFamily: "var(--font-aleo)" }}>
              {title}
            </h2>
          ) : null}
          <div className={`mt-6 grid gap-5 sm:grid-cols-2 ${cols}`}>
            {items.map(({ label, description, href, icon }) => {
              const Icon = TILE_ICONS[icon ?? ""] ?? Landmark;
              return (
                <GuardedLink
                  key={label}
                  href={href}
                  label={label}
                  block
                  className="group relative flex min-h-[220px] overflow-hidden rounded-md border-t-[3px] border-t-[#0066A8] bg-white p-5 text-slate-700 shadow-bcc-panel transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#FFA401]"
                >
                  <Icon
                    className="pointer-events-none absolute -bottom-9 -right-8 h-44 w-44 text-[#0069B4]/[0.055] transition-transform duration-300 group-hover:-translate-x-1 group-hover:-translate-y-1"
                    strokeWidth={1.15}
                    aria-hidden="true"
                  />
                  <div className="relative flex w-full flex-col">
                    <div className="flex items-start gap-3">
                      <Icon className="mt-0.5 h-6 w-6 shrink-0 text-[#005CA9]" strokeWidth={1.75} aria-hidden="true" />
                      <h3 className="text-xl font-bold leading-tight text-[#005CA9]" style={{ fontFamily: "var(--font-aleo)" }}>
                        {label}
                      </h3>
                    </div>
                    {description ? (
                      <p className="mt-3 text-sm leading-6 text-slate-600">{description}</p>
                    ) : null}
                    <span className="mt-auto inline-flex items-center gap-2 pt-5 text-sm font-bold text-[#005CA9]">
                      {english ? "Read more" : "Lire la suite"}
                      <ArrowRight
                        className="h-4 w-4 text-[#FFB323] transition-transform group-hover:translate-x-1"
                        aria-hidden="true"
                      />
                    </span>
                  </div>
                </GuardedLink>
              );
            })}
          </div>
        </div>
      </section>
    );
  }

  if (variant === "gateway") {
    // Carte-passerelle premium : MÊME gabarit visuel que les cartes secteur de
    // /statistiques (SectorGatewayCard) — bleu marine, icône en filigrane dans
    // l'angle, libellé + description en blanc, « Lire la suite → » ambre. Pour
    // les grilles qui ouvrent des sous-rubriques majeures (organes de la Banque).
    const cols = SOFT_COLS[Number(columns)] ?? "lg:grid-cols-3";
    return (
      <section>
        {title ? (
          <h3
            className="mb-4 text-xl md:text-2xl font-semibold text-[#005CA9]"
            style={{ fontFamily: "var(--font-aleo)" }}
          >
            {title}
          </h3>
        ) : null}
        <div className={`grid gap-4 sm:grid-cols-2 ${cols}`}>
          {items.map(({ label, description, href, icon }) => {
            const Icon = TILE_ICONS[icon ?? ""] ?? Landmark;
            return (
              <GuardedLink
                key={label}
                href={href}
                label={label}
                block
                className="group relative flex min-h-[240px] overflow-hidden rounded-md bg-[#0769AC] p-7 text-white ring-1 ring-[#005B99] transition hover:bg-[#005B99] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#FFA401] md:p-8"
              >
                <Icon
                  className="pointer-events-none absolute -bottom-9 -right-8 h-44 w-44 text-white/[0.07] transition-transform duration-300 group-hover:-translate-x-1 group-hover:-translate-y-1"
                  strokeWidth={1.15}
                  aria-hidden="true"
                />
                <div className="relative flex w-full flex-col">
                  <h4 className="text-xl font-bold leading-7" style={{ fontFamily: "var(--font-aleo)" }}>
                    {label}
                  </h4>
                  {description ? (
                    <p className="mt-3 text-[0.95rem] leading-6 text-white/95">{description}</p>
                  ) : null}
                  <span className="mt-auto inline-flex items-center gap-2 pt-6 text-sm font-bold text-white">
                    {english ? "Read more" : "Lire la suite"}
                    <ArrowRight
                      className="h-4 w-4 text-[#FFA401] transition-transform group-hover:translate-x-1"
                      aria-hidden="true"
                    />
                  </span>
                </div>
              </GuardedLink>
            );
          })}
        </div>
      </section>
    );
  }

  if (variant === "soft") {
    const cols = SOFT_COLS[Number(columns)] ?? "lg:grid-cols-4";
    return (
      // Light-blue editorial cards — same palette as the homepage Convertisseur
      // panel (`bg-[#EAF6FF]` + brand-blue title). With a per-item `image` the
      // card gets a 16/10 cover on top (brand "pages connexes" media card);
      // without one it stays a text-only tile. The container has no bottom
      // margin because the surrounding context already controls spacing below.
      <section>
        {title ? (
          <h3
            className="mb-4 text-xl md:text-2xl font-semibold text-[#005CA9]"
            style={{ fontFamily: "var(--font-aleo)" }}
          >
            {title}
          </h3>
        ) : null}
        <div className={`grid gap-4 sm:grid-cols-2 ${cols}`}>
          {items.map(({ label, description, href, image, imageAlt }) => {
            const { src } = resolveImageValue(image);
            const hasImage = Boolean(src?.trim());
            return (
              <GuardedLink
                key={label}
                href={href}
                label={label}
                block
                className="group flex h-full flex-col overflow-hidden rounded-md bg-[#EAF6FF] ring-1 ring-[#005CA9]/15 transition hover:ring-[#005CA9]/40 hover:bg-[#d8ecfb]"
              >
                {hasImage ? (
                  <div className="aspect-[16/10] w-full overflow-hidden">
                    <RuntimeImage
                      src={src as string}
                      alt={imageAlt ?? ""}
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                    />
                  </div>
                ) : null}
                <div className="flex h-full flex-col p-5">
                  <h4 className="text-base md:text-lg font-semibold text-[#005CA9] group-hover:underline">
                    {label}
                  </h4>
                  {description ? (
                    <p className="mt-2 text-base leading-relaxed text-[#0F2849]/80">{description}</p>
                  ) : null}
                  {href ? (
                    <span className="mt-auto inline-flex pt-4 text-[#FFA401]" aria-hidden>
                      <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5" />
                    </span>
                  ) : null}
                </div>
              </GuardedLink>
            );
          })}
        </div>
      </section>
    );
  }

  return (
    <section className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
      {items.map(({ label, href, icon }) => {
        const Icon = resolvePrimaryIcon(label, icon);
        return (
        <GuardedLink
          key={label}
          href={href}
          label={label}
          block
          className="group flex min-h-[180px] rounded-lg bg-[#076FB2] p-6 text-white ring-1 ring-black/5 transition hover:bg-[#0567A7] md:p-7"
        >
          <div className="flex h-full w-full flex-col items-center text-center">
            <div className="flex flex-1 flex-col items-center justify-center">
              <Icon className="h-9 w-9 text-white" strokeWidth={1.65} aria-hidden="true" />
              <div className="mt-4 line-clamp-3 text-xl font-semibold leading-7">
                {label}
              </div>
            </div>
            <span className="mt-5 inline-flex items-center gap-3 text-sm font-bold text-white">
              {english ? "Read more" : "Lire la suite"}
              <ArrowRight className="h-4 w-4 text-[#FFA401] transition-transform group-hover:translate-x-1" aria-hidden="true" />
            </span>
          </div>
        </GuardedLink>
        );
      })}
    </section>
  );
}
